import { Flex, Button, Box, Container, Text } from '@chakra-ui/react'
import useTranslation from 'next-translate/useTranslation'
import SwiperCore, { Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { IFullScreenSliderSection } from '@interfaces'
import { useRouter } from 'next/router'
import { useState, useEffect } from 'react'
import { HeroAspectRatio, MotionBox, MotionText } from '@ui'
import { fadeIn } from '@lib/motion'
import { useInViewMotion } from '@lib/hooks'
import { keyframes } from '@emotion/react'
import { Markup } from 'interweave'

// install Swiper modules
SwiperCore.use([Navigation, Pagination])

// Arka plan görseli 869x579 (~1.5:1). Kabı bu orana sabitleyince
// backgroundSize: contain ile görsel tam oturur, kırpılmaz ve yanlarda boşluk kalmaz.
const SLIDER_BG_RATIO = 869 / 579

const FullScreenSliderSection = ({
  subTitle,
  title,
  description,
  sliderCollection,
  stickyId = 'FullScreenSliderSection',
  containerProps,
  viewAllButtonLabelTx,
  viewAllButtonLabelOnClick,
  isHeroSection = false,
  isCustomTextContent = false,
  customHTMLContent,
  bgImageUrl,
}: IFullScreenSliderSection) => {
  const router = useRouter()
  const { t } = useTranslation()
  const { controls, ref } = useInViewMotion(true)
  const fixedSliderIndex = 0
  const [activeSliderIndex, setActiveSliderIndex] = useState(fixedSliderIndex)
  const isDemo = !!bgImageUrl
  const overlayAnimationControls = isDemo ? 'visible' : controls
  const activeSlide = sliderCollection[activeSliderIndex] ?? sliderCollection[0]
  const activeSlideImageUrl = activeSlide?.imgUrl || bgImageUrl
  const activeSlideMobileImageUrl =
    activeSlide?.mobileImgUrl || activeSlideImageUrl
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const demoSectionMinH: any = { base: 'auto', lg: isDemo ? 'auto' : '0' }
  const demoBgSize: any = isDemo ? 'contain' : { base: 'cover', lg: 'contain' }
  const demoBgImage: string = isDemo
    ? `linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 45%, rgba(0,0,0,0) 70%), url('${activeSlideImageUrl}')`
    : `url('https://drupal.sakipsabancimuzesi.org/sites/default/files/2026-06/WhatsApp%20Image%202026-05-22%20at%2013.47.19.jpeg')`
  const demoSectionSx: any = {
    aspectRatio: { base: 'auto', lg: SLIDER_BG_RATIO },
    _before: { display: { base: 'none', lg: 'block' } },
    '&>*:not(style)': {
      position: { base: 'static', lg: 'absolute' },
    },
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  useEffect(() => {
    const fullScreenSliderSectionBg = `${stickyId}Bg`
    const fullScreenSliderSectionBgNode = document.getElementById(
      fullScreenSliderSectionBg
    )

    if (!fullScreenSliderSectionBgNode) return

    fullScreenSliderSectionBgNode.classList.add('animate__fadeIn')

    const removeFadingClass = () =>
      fullScreenSliderSectionBgNode.classList.remove('animate__fadeIn')

    fullScreenSliderSectionBgNode.addEventListener(
      'animationend',
      removeFadingClass,
      false
    )
  }, [activeSliderIndex, stickyId])

  const handleArtDetail = (path: string) => router.push(path)

  const sliderOverlayContent = (
    <MotionBox
      ref={ref}
      initial="hidden"
      animate={overlayAnimationControls}
      variants={fadeIn.animationContainer}
      position={isDemo ? 'absolute' : 'relative'}
      top={isDemo ? 0 : undefined}
      right={isDemo ? 0 : undefined}
      bottom={isDemo ? 0 : undefined}
      left={isDemo ? 0 : undefined}
      width="full"
      height={isDemo ? 'full' : undefined}
      zIndex={isDemo ? 1 : undefined}
      display={isDemo ? 'flex' : undefined}
      flexDirection={isDemo ? 'column' : undefined}
      pt={
        isDemo
          ? isHeroSection
            ? '70px'
            : { base: 6, lg: 'padding.primary2' }
          : undefined
      }
      pb={isDemo ? 0 : undefined}
    >
      <Container
        height="full"
        display="flex"
        flexDirection={{ base: 'column', lg: 'row' }}
        justifyContent={
          isDemo ? { base: 'flex-start', lg: 'flex-start' } : undefined
        }
        flex={isDemo ? 1 : undefined}
        px={isDemo ? { base: 0, lg: undefined } : undefined}
        position={isDemo ? { base: 'relative', lg: 'static' } : 'relative'}
      >
        <Box
          maxW={{ base: 'full', lg: '610px' }}
          flexShrink={0}
          px={isDemo ? { base: 4, lg: 0 } : undefined}
        >
          {isCustomTextContent ? (
            <Box
              sx={{
                '& h3': {
                  fontSize: { base: 'xl', lg: '3xl' },
                  color: 'red.primary',
                  mb: { base: 1, lg: '0' },
                },
                '& h2': {
                  fontSize: { base: '5xl', lg: '7xl' },
                  color: 'rgba(255, 255, 255, 0.25)',
                  fontWeight: '400',
                  lineHeight: 'normal',
                  mb: { base: 1, lg: '0' },
                },
                '& p': {
                  fontSize: { base: 'sm', lg: 'md' },
                  fontWeight: 'light',
                  color: 'white.primary',
                },
              }}
            >
              <Markup content={customHTMLContent} />
            </Box>
          ) : isDemo && activeSlide ? (
            <>
              <MotionText
                fontSize={{ base: '2xl', lg: '3xl' }}
                color="red.primary"
                as="h3"
                mb={{ base: 1, lg: '0' }}
                variants={fadeIn.up}
              >
                {activeSlide.typeLabel}
              </MotionText>
              <MotionBox
                fontSize={{ base: 'xl', lg: '5xl' }}
                color={{ base: 'white.primary', lg: 'whiteAlpha.primary5' }}
                fontWeight="200"
                lineHeight={{ base: 'short', lg: 'normal' }}
                as="h2"
                mb={{ base: 2, lg: '6px' }}
                variants={fadeIn.up}
                sx={{
                  '& br': {
                    display: { base: 'none', lg: 'block' },
                  },
                }}
              >
                <Markup
                  content={activeSlide.overlayTitle || activeSlide.title}
                />
              </MotionBox>
              <MotionText
                fontSize={{ base: 'sm', lg: 'md' }}
                fontWeight="light"
                color="white.primary"
                lineHeight={{ base: 'tall', lg: 'normal' }}
                variants={fadeIn.up}
              >
                {activeSlide.description}
              </MotionText>
            </>
          ) : (
            <>
              {subTitle && (
                <MotionText
                  fontSize={{ base: '2xl', lg: '3xl' }}
                  color="red.primary"
                  as="h3"
                  mb={{ base: 1, lg: '0' }}
                  variants={fadeIn.up}
                >
                  {subTitle}
                </MotionText>
              )}
              {title && (
                <MotionText
                  fontSize={{ base: 'xl', lg: '5xl' }}
                  color="whiteAlpha.primary5"
                  fontWeight="200"
                  lineHeight="normal"
                  as="h2"
                  mb={{ base: 1, lg: '6px' }}
                  variants={fadeIn.up}
                >
                  {title}
                </MotionText>
              )}
              {description && (
                <MotionText
                  fontSize={{ base: 'sm', lg: 'md' }}
                  fontWeight="light"
                  color="white.primary"
                  variants={fadeIn.up}
                >
                  {description}
                </MotionText>
              )}
            </>
          )}
        </Box>
        <Flex
          flex={isDemo ? { base: 'none', lg: 'none' } : 1}
          alignItems="flex-end"
          flexDirection={{ base: 'column-reverse', lg: 'row' }}
          position="relative"
          width="full"
          mt={{ base: isDemo ? 0 : 'padding.primary', lg: '0' }}
          alignSelf={isDemo ? { base: 'stretch', lg: 'auto' } : undefined}
          sx={
            isDemo
              ? {
                  '@media (max-width: 1169px)': {
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: '100%',
                    ml: 0,
                  },
                  '@media (min-width: 1170px)': {
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    width: 'auto',
                    ml: 0,
                  },
                }
              : {
                  width: 'calc(100% + 60px)',
                  ml: '-30px',
                  '@media (min-width: 1170px)': {
                    position: 'absolute',
                    right: '-40px',
                    bottom: 0,
                    width: '790px',
                  },
                }
          }
        >
          {viewAllButtonLabelTx && viewAllButtonLabelOnClick && (
            <Box width={{ base: 'full', lg: 'auto' }} display="inline-block">
              <Button
                width={{ base: 'full', lg: '240px' }}
                height={{ base: 20, lg: '100px' }}
                onClick={viewAllButtonLabelOnClick}
              >
                {viewAllButtonLabelTx}
              </Button>
            </Box>
          )}

          <Box
            width={{ base: 'full', lg: 'auto' }}
            className="DiscoverSSMArtSwiper"
            sx={{
              '&.DiscoverSSMArtSwiper .swiper-container': {
                width: 'full',
                maxW: { base: 'full', lg: '520px' },
                height: 'full',
                pl: { base: 0, lg: '0' },
                ml: { base: 0, lg: 'auto' },
              },
              '&.DiscoverSSMArtSwiper .swiper-slide': {
                width: { base: '100%', lg: 'drawer.lg' },
                maxWidth: '100%',
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                backgroundColor: 'white.primary',
                overflow: 'hidden',
                boxSizing: 'border-box',
              },
              '.discover-art-swiper-button-next, .discover-art-swiper-button-prev':
                {
                  width: '16px',
                  height: 'auto',
                  color: 'red.primary2',
                },
              '.discover-art-swiper-button-next:after, .discover-art-swiper-button-prev:after':
                {
                  fontSize: 'md',
                },
              '.discover-art-swiper-button-next.swiper-button-disabled, .discover-art-swiper-button-prev.swiper-button-disabled':
                {
                  opacity: 1,
                },
              '.discover-art-swiper-button-next': {
                position: 'absolute',
                bottom: '27px',
                top: 'auto',
                right: '80px',
              },
              '.discover-art-swiper-button-prev': {
                position: 'absolute',
                top: 'auto',
                bottom: '27px',
                right: '115px',
                left: 'auto',
              },
            }}
          >
            <Swiper
              initialSlide={fixedSliderIndex}
              onSlideChange={(swiper) =>
                setActiveSliderIndex(swiper.activeIndex)
              }
              slidesPerView={1}
              spaceBetween={20}
              pagination={{
                el: '.swiper-pagination',
                type: 'fraction',
              }}
              navigation={{
                nextEl: '.discover-art-swiper-button-next',
                prevEl: '.discover-art-swiper-button-prev',
              }}
              allowTouchMove={false}
              grabCursor={false}
            >
              {sliderCollection.map((slide) => (
                <SwiperSlide key={slide.id}>
                  <Flex
                    flexDirection="column"
                    px={{ base: 4, lg: 'padding.primary' }}
                    pt={{ base: 4, lg: 'padding.primary' }}
                    mb={{ base: isDemo ? 0 : 4, lg: '107px' }}
                    width="100%"
                    maxW="100%"
                    minW={0}
                    overflow="hidden"
                  >
                    <Text
                      fontSize={{ base: 'sm', lg: 'xs' }}
                      color="red.primary"
                      mb={{ base: '6px', lg: '11px' }}
                      as="span"
                    >
                      {slide.typeLabel ||
                        t(`common:newsAndBlogTypes.${slide.type}`)}
                    </Text>

                    <Text
                      fontSize={{ base: 'sm', lg: 'sm' }}
                      mb={{ base: '7px', lg: '11px' }}
                      color="grey.primary"
                      fontWeight="medium"
                      as="span"
                    >
                      {slide.date}
                    </Text>
                    <Text
                      fontSize={{ base: 'md', lg: 'md' }}
                      color="black.primary3"
                      mb={{ base: 3, lg: 'padding.primary' }}
                      as="h6"
                      w="100%"
                      maxW="100%"
                      wordBreak="break-word"
                      overflowWrap="anywhere"
                    >
                      {slide.title + ' ' + slide.subtitle}
                    </Text>
                    <Box
                      fontSize={{ base: 'sm', lg: 'sm' }}
                      color="black.primary3"
                      as="p"
                      fontWeight="normal"
                      lineHeight="short"
                      w="100%"
                      maxW="100%"
                      wordBreak="break-word"
                      overflowWrap="anywhere"
                    >
                      <Markup content={slide.shortDescription} />
                    </Box>
                  </Flex>
                  <Flex>
                    <Button
                      variant="primary"
                      size="newsletter"
                      bgColor="red.primary"
                      width="auto"
                      minW="unset"
                      h="auto"
                      mr="5px"
                      mt="15px"
                      py={{ base: '27px', lg: undefined }}
                      px={{ base: '14px', lg: undefined }}
                      onClick={() =>
                        handleArtDetail(
                          slide.mediumLink ? slide.mediumLink : slide.url
                        )
                      }
                    >
                      {t('common:buttonExamineLabel')}
                    </Button>
                  </Flex>
                </SwiperSlide>
              ))}

              <Box
                role="button"
                aria-label="Discover ssm art slider prev button"
                className="swiper-button-prev discover-art-swiper-button-prev"
              />
              <Box
                role="button"
                aria-label="Discover ssm art slider next button"
                className="swiper-button-next discover-art-swiper-button-next"
              />
            </Swiper>
          </Box>
        </Flex>
      </Container>
    </MotionBox>
  )

  if (isDemo) {
    return (
      <Box
        as="section"
        id={stickyId}
        position="relative"
        width="full"
        overflow="hidden"
        {...containerProps}
      >
        <Box display="block" width="100%" lineHeight={0}>
          <Box
            as="img"
            id={`${stickyId}Bg`}
            src={activeSlideMobileImageUrl}
            alt=""
            width="100%"
            height="auto"
            display={{ base: 'block', lg: 'none' }}
            sx={{
              '&.animate__fadeIn': {
                animation: `${bgFadeIn} 0.4s ease-in-out forwards`,
              },
            }}
          />
          <Box
            as="img"
            src={activeSlideImageUrl}
            alt=""
            width="100%"
            height="auto"
            display={{ base: 'none', lg: 'block' }}
            sx={{
              '&.animate__fadeIn': {
                animation: `${bgFadeIn} 0.4s ease-in-out forwards`,
              },
            }}
          />
        </Box>
        <Box
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          pointerEvents="none"
          background={{
            base: 'linear-gradient(180deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 28%, rgba(0,0,0,0.2) 48%, rgba(0,0,0,0.35) 72%, rgba(0,0,0,0.82) 100%)',
            lg: 'linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 45%, rgba(0,0,0,0) 70%)',
          }}
          zIndex={0}
        />
        {sliderOverlayContent}
      </Box>
    )
  }

  return (
    <HeroAspectRatio
      id={stickyId}
      as="section"
      minH={demoSectionMinH}
      sx={demoSectionSx}
    >
      <Box
        width="full"
        height="full"
        overflow="hidden"
        position="relative"
        pt={isHeroSection ? '70px' : 'padding.primary2'}
        zIndex="1"
        {...containerProps}
      >
        <Box
          id={`${stickyId}Bg`}
          position="absolute"
          top="0"
          right="0"
          bottom="0"
          left="0"
          backgroundPosition="center"
          backgroundSize={demoBgSize}
          backgroundRepeat="no-repeat"
          backgroundImage={demoBgImage}
          zIndex={-1}
          sx={{
            '&.animate__fadeIn': {
              animation: `${bgFadeIn} 0.4s ease-in-out forwards`,
            },
          }}
        />
        {sliderOverlayContent}
      </Box>
    </HeroAspectRatio>
  )
}

export default FullScreenSliderSection

const bgFadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`
